<template>
  <div v-if="isAssistConversation" class="assist-input">
    <div class="input-tabs">
      <div
        v-for="tab in tabs"
        :key="tab.id"
        class="tab-item"
        :class="{
          selected: selectedTab === tab.id,
          'inputs-disabled': inputsDisabled,
        }"
        @click="handleTab(tab.id)"
      >
        <slot v-if="shouldDisplayPromptsTab">
          <BbIcon size="small" :name="tab.icon" />
          <TextBody :i18n-key="tab.label" tag="span" :body-type="1" weight="medium" />
        </slot>
      </div>
    </div>

    <div class="inputs-wrapper" :class="{ 'inputs-disabled': inputsDisabled }">
      <InputDisabledBannerInfo v-if="inputsDisabled" />

      <div v-if="displayPromptCardsList">
        <PromptCardSelectCategory
          v-if="categories?.length"
          :isMoreCardsModalOpened="isMoreCardsModalOpened"
          @select-categories="selectCategories"
        />
        <PromptCardsList
          :prompts="prompts"
          :showMoreButton="true"
          @open-more="isMoreCardsModalOpened = true"
          @select-prompt="onSelectPromptEvent"
        />
      </div>

      <hr v-if="!displayPromptsInNewTab && messages.length === 0 && selectedTab !== 2" class="divider" />

      <InputField
        v-show="selectedTab === 0"
        :rows="5"
        :is-disabled="inputsDisabled"
        v-model="sharedValueChat"
        @onSubmit="onSubmit(inputTabValues.chat)"
      />
      <PromptCardInput
        v-if="displayPromptCardsInput"
        v-model="sharedValuePrompt"
        :external-disabled="inputsDisabled"
        :filtersConfig="filtersConfig"
        @onSubmit="onSubmit(inputTabValues.prompt)"
      />

      <div v-if="selectedTab === 2">
        <FilterFormMultiselect :filtersConfig="filtersConfig" :allFilterValues="allFilterValues" :type="filterType" />
      </div>
    </div>
  </div>

  <div v-else class="inputs-wrapper" :class="{ 'inputs-disabled': inputsDisabled }">
    <InputDisabledBannerInfo v-if="inputsDisabled" />
    <InputField :is-disabled="inputsDisabled" v-model="sharedValueChat" @onSubmit="onSubmit(inputTabValues.chat)" />
  </div>

  <PromptCardsModal
    v-if="isMoreCardsModalOpened"
    :isOpen="isMoreCardsModalOpened"
    @select-prompt="onSelectPromptEvent"
    @select-categories="selectCategories"
    @close="isMoreCardsModalOpened = false"
  />
</template>

<script>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { marked } from 'marked';

import { Routes } from '~/constants/routes';
import { inputTabValues, tabs } from '~/constants/inputTabs';
import { isSuggestedCategory } from '~/constants/conversationModels';
import { FILTER_TYPE } from '~/constants/documents';
import { GetUserData } from '~/constants/getters';

import PromptCardInput from '~/components/conversation/quickPrompting/PromptCardInput.vue';
import InputField from '~/components/conversation/InputField.vue';
import TextBody from '~/components/common/TextBody.vue';
import { useSanitize } from '~/composables/useSanitize';
import useModals from '~/composables/useModals';
import useConversations from '~/composables/useConversations';
import PromptCardsModal from '~/components/conversation/quickPrompting/PromptCardsModal.vue';
import useQuickPrompts from '~/composables/useQuickPrompts';
import PromptCardsList from '~/components/conversation/quickPrompting/PromptCardsList.vue';
import PromptCardSelectCategory from '~/components/conversation/quickPrompting/PromptCardSelectCategory.vue';
import FilterFormMultiselect from '~/services/filter-form-service/components/FilterFormMultiselect.vue';
import useFormFilters from '~/composables/useFormFilters';
import InputDisabledBannerInfo from '~/components/conversation/InputDisabledBannerInfo.vue';

export default {
  components: {
    FilterFormMultiselect,
    PromptCardSelectCategory,
    PromptCardsList,
    PromptCardInput,
    InputField,
    TextBody,
    PromptCardsModal,
    InputDisabledBannerInfo,
  },
  emits: ['onSubmit', 'select-prompt'],
  setup(props, { emit }) {
    const filterService = inject('filterFormService');
    const filterType = FILTER_TYPE.document;

    const route = useRoute();
    const store = useStore();

    const { sanitize } = useSanitize(true);
    const { setShowFilters } = useModals();
    const { currentConversation } = useConversations();

    const userData = computed(() => store.getters[`authStore/${GetUserData}`]);
    const inputsDisabled = computed(
      () =>
        !!(
          isAssistConversation &&
          currentConversation.value?.userId &&
          currentConversation.value?.userId !== userData.value?.id
        ),
    );
    const {
      setDisplayPromptsInNewTab,
      displayPromptsInNewTab,
      onSelectPrompt,
      prompts,
      currentPromptCard,
      getPromptCardsByCategory,
      getCategories,
      setSelectedCategories,
      categories,
      selectedCategories,
    } = useQuickPrompts();
    const { formFilters } = useFormFilters();

    const applyLimit = (array) => {
      const widthLimit = window.innerWidth > 1120 ? 8 : window.innerWidth > 754 ? 2 : 2;
      const heightLimit =
        window.innerHeight > 900 ? 8 : window.innerHeight > 700 ? 5 : window.innerHeight > 400 ? 3 : 2;
      return array?.slice(0, Math.min(widthLimit, heightLimit));
    };

    const currentRouteName = route.name;
    const formFiltersForType = computed(() => formFilters.value[filterType] || {});
    const messages = computed(() => currentConversation.value.messages);
    const displayPromptCardsList = computed(
      () =>
        (selectedTab.value === 0 && messages.value.length === 0) ||
        (!currentPromptCard.value?.id && selectedTab.value === 1),
    );
    const displayPromptCardsInput = computed(
      () => displayPromptsInNewTab.value && currentPromptCard.value?.id && selectedTab.value === 1,
    );
    const currentPrompts = ref([]);

    const sharedValue = ref({
      chat: '',
      prompt: '',
    });

    const sharedValueChat = computed({
      get: () => sharedValue.value.chat,
      set: (val) => (sharedValue.value.chat = val),
    });

    const sharedValuePrompt = computed({
      get: () => sharedValue.value.prompt,
      set: (val) => (sharedValue.value.prompt = val),
    });

    const selectedTab = ref(0);
    const isMoreCardsModalOpened = ref(false);
    const windowWidth = ref(window.innerWidth);
    const filtersConfig = ref([]);
    const allFilterValues = ref([]);
    const isAssistConversation = currentRouteName === Routes.Conversation.name;

    const shouldDisplayPromptsTab = () =>
      (messages.value.length === 0 && currentPromptCard.value?.id) || displayPromptsInNewTab.value || true;

    const handleTab = (index) => {
      if (index === tabs.find((item) => item.type === 'filters')?.id) {
        setShowFilters(true);
      }

      selectedTab.value = index;

      if (index === 0) {
        setDisplayPromptsInNewTab(false);
      }
    };

    const onSelectPromptEvent = (prompt) => {
      onSelectPrompt({ prompt, filtersConfig: filtersConfig.value });
    };

    const onSubmit = (valueType) => {
      const sanitizedInput = sanitize(marked(sharedValue.value[valueType]));
      emit('onSubmit', sanitizedInput);
      // wait for message to be sent in order to make sure state is not cleared before
      setTimeout(() => {
        selectedTab.value = 0;
      }, 500);
    };

    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
      currentPrompts.value = applyLimit(prompts.value);
    };

    const selectCategories = (categoryIds) => {
      if (selectedCategories.value?.[0] !== categoryIds?.[0]) {
        setSelectedCategories([categoryIds[0]]);
        fetchPromptsByCategory([categoryIds[0]]);
      }
    };

    let fetchSeqNum = 0;
    const fetchPromptsByCategory = async (categories = []) => {
      const seq = ++fetchSeqNum;
      const categoriesIds = categories.filter((id) => id !== isSuggestedCategory.id);
      const prompts = await getPromptCardsByCategory({
        categories: categoriesIds || [],
        ...(!categories?.length || categories?.includes(isSuggestedCategory.id) ? { isSuggested: true } : {}),
      });
      if (seq === fetchSeqNum) {
        currentPrompts.value = applyLimit(prompts);
      }
    };

    onMounted(async () => {
      window.addEventListener('resize', updateWidth);

      if (isAssistConversation) {
        await getCategories();
        await fetchPromptsByCategory();

        const filterForm = await filterService.getFilterForm();

        filtersConfig.value = filterForm;
        allFilterValues.value = filterForm;
      }
    });
    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateWidth);
    });

    watch(
      () => formFiltersForType.value,
      async (newVals, oldVals) => {
        if (JSON.stringify(newVals) !== JSON.stringify(oldVals)) {
          filtersConfig.value = await filterService.getFilterForm(filterType);
        }
      },
      { deep: true },
    );

    watch(displayPromptsInNewTab, (newValue) => {
      if (newValue === true) {
        selectedTab.value = 1;
      }
    });

    watch(
      selectedTab,
      (newValue) => {
        if (newValue === 2) {
          setDisplayPromptsInNewTab(false);
        }
      },
      { immediate: true },
    );

    watch(
      selectedTab,
      (newValue) => {
        if (newValue === 1) {
          setDisplayPromptsInNewTab(true);
        }
      },
      { immediate: true },
    );

    return {
      sharedValueChat,
      sharedValuePrompt,
      sharedValue,
      isAssistConversation,
      tabs,
      selectedTab,
      handleTab,
      onSubmit,
      displayPromptsInNewTab,
      onSelectPromptEvent,
      isMoreCardsModalOpened,
      prompts: currentPrompts,
      shouldDisplayPromptsTab,
      messages,
      displayPromptCardsList,
      displayPromptCardsInput,
      selectCategories,
      categories,
      filtersConfig,
      allFilterValues,
      filterType,
      inputsDisabled,
      inputTabValues,
    };
  },
};
</script>

<style scoped lang="scss">
.assist-input {
  border-radius: 24px;
  border: 1px solid $bb-neutral-4;

  .bb-textarea {
    padding: $bb-spacing-6;

    :deep(.v-field__outline) {
      display: none !important;
    }

    :deep(.v-field__append-inner) {
      background: red;

      button {
        position: absolute;
        bottom: -16px;
        right: -16px;
      }
    }
  }

  .input-tabs {
    display: flex;
    align-items: center;
    background: $bb-neutral-6;
    border-radius: 24px 24px 0 0;

    .tab-item {
      padding: $bb-spacing-4;
      display: flex;
      align-items: center;
      gap: $bb-spacing-2;
      cursor: pointer;

      &:last-of-type {
        margin-left: auto;
      }
    }

    .selected {
      background: $bb-neutral-0;
      border-radius: 24px 24px 0 0;
    }

    .tab-disabled {
      opacity: 0.4;
      cursor: unset;
      pointer-events: none;
    }
  }
}

.divider {
  border: 0;
  height: 1px;
  background-color: #ccc;
}

.inputs-disabled {
  pointer-events: none;
  position: relative;
}

.inputs-wrapper {
  margin-top: $bb-spacing-2;
}
</style>
